import { debounceTime, Subject, Subscription } from "rxjs";
import { inject, singleton } from "tsyringe";
import { ExtensionContext } from "../../common/models/extension-config";
import { Module } from "../../common/module";
import { Snippets } from "../../common/snippets";
import { CommonModule } from "../common.module";
import { ApiModule } from "../common/api/api.module";
import { SiteRoleType } from "../common/api/autogenerated";
import { DashboardTooltipButtonManagerModule } from "../common/dashboard-tooltip-button-manager/dashboard-tooltip-button-manager.module";
import { RequestInterceptorModule } from "../common/request-interceptor.module";
import { UrlChangeListenerModule } from "../common/url-change-listener.module";
import { ChangeRoleCommonModule } from "./changerole-common.module";
@singleton()
export class ChangeRoleProfileButtonModule extends Module {
    public name: string = 'ChangeRoleProfileButtonModule';
    public acceptedLicenseFlags: string[] = ['ChangeRole',  'NetflixIntegrations'];
    public shouldRun(context: ExtensionContext): boolean {
        return context === ExtensionContext.vizportal;
    }
    constructor(
        @inject(CommonModule) private common: CommonModule,
        @inject(ChangeRoleCommonModule) private changeRoleCommon: ChangeRoleCommonModule,
        @inject(UrlChangeListenerModule) private urlChange: UrlChangeListenerModule,
        @inject(RequestInterceptorModule) private requestInterceptor: RequestInterceptorModule) {
        super([common, changeRoleCommon, requestInterceptor, urlChange]);
    }

    private menuButton_selector = '[class^=PageHeader_right-item] button[data-tb-test-id=flyout-list-menu-Button]';
    private contexMenu_selector = '[data-tb-test-id=Overlay-Root] [data-tb-test-id=flyout-list-menu-ListMenu]';
    private button_snippet = '<div class="f16dpfvc" data-tb-test-id="flyout-list-menu-TextMenuItem" style="height: 20px; padding-left: 6px; padding-right: 6px; line-height: 20px; position: relative; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; user-select: none; cursor: default;">' +
        '<span class="loading" style="display: inline-block; transform-origin: top left; transform: scale(0.6); vertical-align: middle; height: 14px; width: 18px; display: none;">' + Snippets.loadingGif + '</span>' +
        '<span class="frvoegc">@Text</span>' +
        '</div>';
    private showButtonRequest = new Subject<void>();
    public async run(): Promise<void> {
        this.showButtonRequest.pipe(debounceTime(100)).subscribe(() => this.showButton());
        let httpSub: Subscription | undefined;

        this.changeRoleCommon.configEvent.subscribe(config => {
            this.removeButton();
            // window.removeEventListener()
            if (httpSub) {
                httpSub.unsubscribe();
            }

            //if (proBtn)

            if (!config) {
                return;
            }
            // if user is site or server admin, skip
            const specialRoles = [SiteRoleType.ServerAdministrator, SiteRoleType.SiteAdministrator, SiteRoleType.SiteAdministratorCreator, SiteRoleType.SiteAdministratorExplorer];
            if (specialRoles.includes(config.currentRole!)) {
                return;
            }
            // if there are no available roles, skip
            if (config.availableRoles!.filter(e => e !== config.currentRole).length < 1) {
                return;
            }
            this.showButtonRequest.next();
            this.urlChange.event.subscribe(() => this.showButtonRequest.next());
            // if getsessioninfo is fired, the profile button is removed, so we re-draw
            httpSub = this.requestInterceptor.onTableauRequest.subscribe(r => {
                if (r.dataObject?.method === 'switchSite' ||
                    r.dataObject?.method === 'getSessionInfo') {
                    r.request.addEventListener('load', async () => {
                        if (r.request.status === 200) {
                            await this.common.requestAnimationFrame();
                            this.showButtonRequest.next();
                        }
                    }, false);
                }
            })

        });
    }

    private removeButton() {
        const btn = document.getElementById('stm-changerole-menubutton');
        if (btn) {
            btn.remove();
        }
    }
    private async showButton() {
        // 1. We find the profile button.
        // 2. We subscribe to the click of the profile button
        // 3. When its clicked, we check if the context menu has been rendered (after a frame)
        // 4. If its rendered, add the button

        // 1. We find the profile button.
        const profileButton = await this.common.waitForElement(this.menuButton_selector, 60, document) as HTMLElement;
        if (!profileButton) {
            return;
        }
        // 2. We subscribe to the click of the profile button 
        profileButton.onclick = async () => {
            await this.common.requestAnimationFrame();
            // check if context menu is rendered
            const contextMenu = await this.common.waitForElement(this.contexMenu_selector, 10, document);
            if (!contextMenu) {
                return;
            }
            const existingButton = document.getElementById('stm-changerole-menubutton');
            if (existingButton) {
                return;
            }
            const btn = document.createElement('div');
            btn.id = 'stm-changerole-menubutton';
            btn.setAttribute('data-tb-test-id', 'flyout-list-menu-changeRole-MenuItem');
            btn.setAttribute('role', 'menuitem');
            btn.tabIndex = -1;
            btn.style.outline = 'none';
            btn.innerHTML = this.button_snippet.replace('@Text', 'Manage My Role');
            btn.onclick = () => this.btnClick(btn);
            btn.onmouseenter = () => {
                // highlight color our button

                (btn.children[0] as HTMLElement).style.background = 'rgb(235, 235, 235)';
                // remove highlight color from all other buttons (angular does not do it for us)
                btn.parentElement!.childNodes.forEach(c => {
                   
                    const child = c as HTMLElement;
                    if (!child || child.id === btn.id) {
                        return;
                    }
                    const subChild = child.children[0] as HTMLElement;
                    if (subChild) {
                        subChild.classList.remove('highlight');
                    }

                });
                btn.children[0].classList.add('highlight');
            }
            btn.onmouseleave = () => { (btn.children[0] as HTMLElement).style.background = ''; btn.children[0].classList.remove('highlight'); }
            contextMenu.insertBefore(btn, contextMenu.children[2]);


        }


    }
    private async btnClick(btn: HTMLElement) {
        this.common.queryElement(btn, '.loading')!.style.display = 'inline-block';
        try {
            await this.changeRoleCommon.showDialog();
            const menubutton = document.querySelector(this.menuButton_selector) as HTMLElement;
            const contextmenu = document.querySelector(this.contexMenu_selector);
            // if context menu is showing, and we got button, hide context menu by clicking on the button

            if (menubutton && contextmenu) {
                menubutton.click();
                menubutton.blur();
            }
        } catch (e) {
            this.log.e('Failed to show change role dialog', e);
            alert('Failed to open Change role dialog');
        } finally {
            this.common.queryElement(btn, '.loading')!.style.display = 'none';
        }
    }
}